'use client';

import { ENGAGEMENT_EVENTS } from '@repo/analytics';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { trackEvent } from '../track-event';

const DEPTH_BUCKETS = [25, 50, 75, 100] as const;

function computeScrollPercent(scrollY: number, scrollableHeight: number): number {
  if (scrollableHeight <= 0) {
    return 0;
  }
  return (scrollY / scrollableHeight) * 100;
}

export function useScrollDepthTracking(): void {
  const pathname = usePathname();
  const firedRef = useRef<Set<number>>(new Set());
  const documentHeightRef = useRef(0);
  const viewportHeightRef = useRef(0);

  useEffect(() => {
    firedRef.current = new Set();
    if (typeof window === 'undefined') {
      return;
    }
    const recomputeCachedHeights = (): void => {
      documentHeightRef.current = document.documentElement.scrollHeight;
      viewportHeightRef.current = window.innerHeight;
    };
    recomputeCachedHeights();
    const handleScroll = (): void => {
      const scrollableHeight = documentHeightRef.current - viewportHeightRef.current;
      const percent = computeScrollPercent(window.scrollY, scrollableHeight);
      for (const bucket of DEPTH_BUCKETS) {
        if (percent >= bucket && !firedRef.current.has(bucket)) {
          firedRef.current.add(bucket);
          trackEvent(ENGAGEMENT_EVENTS.scrollDepth, { depth: bucket, path: pathname ?? '' });
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', recomputeCachedHeights, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', recomputeCachedHeights);
    };
  }, [pathname]);
}
